import Link from 'next/link'
import cn from 'classnames'

const NumberPagination = ({
  current,
  total,
  linkGeneration,
}: {
  current: number
  total: number
  linkGeneration: (num: number) => string
}): JSX.Element => {
  const pages = []

  for (let index = 1; index <= total; index++) {
    const link = current != index ? linkGeneration(index) : undefined
    pages.push(
      <Number
        key={index}
        highlight={current === index}
        href={link}
        pageIndex={index}
      >
        {index.toString()}
      </Number>
    )
  }

  return (
    <div className="flex flex-wrap">
      <Number
        href={current > 1 ? linkGeneration(current - 1) : undefined}
        pageIndex={current - 1}
      >
        <svg
          className={`w-4 h-4 ${
            current === 1 ? 'opacity-50 cursor-not-allowed' : ''
          }`}
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M15.75 19.5L8.25 12l7.5-7.5"
          />
        </svg>
      </Number>
      {pages}
      <Number
        href={current < total ? linkGeneration(current + 1) : undefined}
        pageIndex={current + 1}
      >
        <svg
          className={`w-4 h-4 ${
            current >= total ? 'opacity-50 cursor-not-allowed' : ''
          }`}
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M8.25 4.5l7.5 7.5-7.5 7.5"
          />
        </svg>
      </Number>
    </div>
  )
}

export const Number = ({
  href,
  highlight = false,
  pageIndex,
  children,
}: {
  href?: string
  highlight?: boolean
  pageIndex?: number
  children: string | JSX.Element
}): JSX.Element => {
  const className = cn(
    'flex items-center justify-center m-2 w-9 h-9 font-medium text-center leading-9 align-text-top border rounded-sm',
    highlight
      ? 'font-bold bg-gold border-gold text-goldInnerText'
      : 'font-medium border-gray-300'
  )
  const linkProps = {}

  if (pageIndex !== 1) {
    linkProps['rel'] = 'nofollow'
  }

  return href ? (
    <Link href={href} className={className} {...linkProps}>
      {children}
    </Link>
  ) : (
    <span className={className}>{children}</span>
  )
}

export default NumberPagination
